import { useCallback, useEffect, useMemo, useState } from 'react';
import ContactInfo from '../components/FormFields/ContactInfo';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { HR } from 'foundations-components/transitional/components/HR';
import { H4 } from 'foundations-components/transitional/components/H4';
import ShippingInfo from '../components/FormFields/ShippingInfo';
import { addHsTrackingCode, trackFormViewAnalyticEvent } from '../utils/analytics';
import { LAYOUTS, getIframeLayout } from '../utils/queryParams';
import { addGoogleAnalyticsTrackingCode } from '../utils/googleAnalytics';
import { addRecaptchaScript, getIsRecaptchaEnabled } from '../utils/googleRecaptcha';
import { PaymentElementSection } from '../components/PaymentInfo/PaymentElementSection';
import PaymentAmount from '../components/PaymentInfo/PaymentAmount';
import partition from 'hs-lodash/partition';
import { useSelector } from 'react-redux';
import ConfirmPaymentButton from '../components/ConfirmPaymentButton';
import BillingInfo from '../components/FormFields/BillingInfo';
import FormSubmitErrorMsgs from '../components/FormSubmitErrorMsgs';
import MaybeExpiredFutureLineItemsAlert from '../components/FutureCharges/MaybeExpiredFutureLineItemsAlert';
import MaybeFutureChargesTotals from '../components/FutureCharges/MaybeFutureChargesTotals';
import Total from '../components/Total/Total';
import { INTERACTION_SECTIONS, INTERACTION_TARGETS } from '../constants/PageInteractionTracking';
import { useOptionalStates } from '../hooks/OrderSummary/useOptionalStates';
import { useTransactionLimits } from '../hooks/useTransactionLimits';
import { getBuyerOverridesIsLoading } from '../state/BuyerOverrides/selectors';
import { isAtCheckoutLineItem } from '../types/utils/typeguards';
import { getIsAnyRecurring } from '../utils/recurring';
import { getIsZeroCheckoutValid, getCanSubmitPayment } from '../utils/validations';
import { getWalletType } from '../utils/wallet';
import { usePaymentFormContext } from '../context/PaymentFormContext';
import usePageInteractionTracking from '../hooks/usePageInteractionTracking';
import { useSubmitPayment } from '../hooks/submitPayment';
import { useValidationErrors } from '../hooks/useValidationErrors';
import TLSEncryptionText from '../components/TLSEncryptionText';
import { getShouldCalculateTaxOnAddrFieldChange } from '../hooks/useCalculateTax';
import useCalculateTax from '../hooks/useCalculateTax';
import TermsOfServiceInputs from '../components/MerchantTermsAndConditions/TermsOfServiceInputs';
import TermsOfServiceMessages from '../components/MerchantTermsAndConditions/TermsOfServiceMessages';
import { getCalculateTaxLoading } from '../state/calculateTaxSlice';
import { useTermsOfServiceDisplay } from '../hooks/useTermsOfServiceDisplay';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PaymentContainer = ({
  checkoutSession
}) => {
  const checkoutSessionJS = checkoutSession.toJS();
  const submitObject = useSubmitPayment();
  const {
    formState,
    dispatch
  } = usePaymentFormContext();
  const stripePaymentMethod = formState.get('stripePaymentMethod');
  const [showRequiredFieldsAlert, setShowRequiredFieldsAlert] = useState(true);
  const [billedAtCheckoutLineItems] = partition(checkoutSessionJS.lineItems, isAtCheckoutLineItem);
  const {
    selectedItems: selectedOptionalLineItems
  } = useOptionalStates(billedAtCheckoutLineItems);
  const {
    trackFirstFormFieldInteraction
  } = usePageInteractionTracking();
  const {
    isValid,
    loading: validationErrorsLoading
  } = useValidationErrors();
  const {
    limits
  } = useTransactionLimits();
  const maximumAmountForPaymentMethod = limits && limits.maximumAmountForPaymentMethod;
  const isZeroCheckout = getIsZeroCheckoutValid(checkoutSessionJS.subtotalsPublicResponse.paymentAmount, checkoutSessionJS.lineItems);
  const loadingBuyerOverrides = useSelector(getBuyerOverridesIsLoading);
  const isCalculateTaxLoading = useSelector(getCalculateTaxLoading);
  const policySettings = checkoutSessionJS.policySettingsData;
  const {
    shouldShowTermsOfServiceAcknowledgementCheckbox,
    shouldShowClickToCancelCheckbox,
    shouldDisplayPMOFCheckbox,
    shouldDisplayPMOFMessage,
    shouldShowTermsOfServiceInputs,
    shouldShowTermsOfServiceMessages
  } = useTermsOfServiceDisplay();
  const submitEnabled = useMemo(() => {
    if (!loadingBuyerOverrides && !isCalculateTaxLoading && isValid !== undefined && !validationErrorsLoading) {
      return getCanSubmitPayment(formState, checkoutSession, isValid, shouldShowTermsOfServiceAcknowledgementCheckbox, shouldDisplayPMOFCheckbox, shouldShowClickToCancelCheckbox, stripePaymentMethod);
    }
    return false;
  }, [checkoutSession, formState, isValid, loadingBuyerOverrides, shouldShowTermsOfServiceAcknowledgementCheckbox, shouldDisplayPMOFCheckbox, stripePaymentMethod, validationErrorsLoading, isCalculateTaxLoading, shouldShowClickToCancelCheckbox]);
  const walletType = getWalletType(stripePaymentMethod);
  const collectShippingAddress = checkoutSessionJS.shippableCountries && !!checkoutSessionJS.shippableCountries.length;
  const collectFullBillingAddress = checkoutSessionJS.collectFullBillingAddress;
  const iFrameLayout = getIframeLayout();
  const isATPortal = checkoutSessionJS.isATPortal;
  const checkoutIntegrations = checkoutSessionJS.checkoutIntegrations;
  const googleAnalyticsData = checkoutIntegrations && checkoutIntegrations.googleAnalyticsIntegration;
  const isRecaptchaEnabled = getIsRecaptchaEnabled(checkoutSession);
  const isPartialPaymentsEnabled = checkoutSessionJS.partialPaymentsInfo && checkoutSessionJS.partialPaymentsInfo.isPartialPaymentsEnabled;
  useEffect(() => {
    if (isRecaptchaEnabled) {
      addRecaptchaScript(isATPortal);
    }
    if (!checkoutSessionJS.sourceUrl || !checkoutSessionJS.portalId) {
      return;
    }
    // Only add the tracking code + track pageView / formViews for UNPAID checkout
    // sessions. Paid checkout sessions should not fire analytic events
    addHsTrackingCode(checkoutSession, !!iFrameLayout);
    trackFormViewAnalyticEvent(checkoutSession, !!iFrameLayout);
    // Only execute this once so we add the script and run the tracking code once

    if (googleAnalyticsData && googleAnalyticsData.isEnabled) {
      addGoogleAnalyticsTrackingCode(googleAnalyticsData.measurementId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Respond to external changes in window.__hsUserToken
  const hsUserToken = window.__hsUserToken;
  useEffect(() => {
    // This effect runs when window.__hsUserToken changes externally
    // We don't need to do anything here since the tracking code will use the updated value
    // This just ensures the component re-renders when the external token changes
  }, [hsUserToken]);
  const {
    debouncedCallCalculateTax: calculateTax
  } = useCalculateTax();
  const shouldCalculateTax = useCallback((name, value) => getShouldCalculateTaxOnAddrFieldChange(name, value, formState.get('billingAddress').toJS(), checkoutSessionJS), [formState, checkoutSessionJS]);
  const handleChange = (name, {
    target: {
      value
    }
  }) => {
    trackFirstFormFieldInteraction({
      section: INTERACTION_SECTIONS.billingAddress,
      target: INTERACTION_TARGETS[name]
    });
    dispatch({
      type: 'setBillingAddressField',
      data: {
        name,
        value
      }
    });
    if (!collectShippingAddress && shouldCalculateTax(name, value)) {
      const billingAddress = Object.assign({}, formState.get('billingAddress').toJS(), {
        line1: formState.get('billingAddress').get('address'),
        line2: formState.get('billingAddress').get('address2'),
        postalCode: name === 'postalCode' ? value : formState.get('billingAddress').get('postalCode'),
        country: name === 'country' ? value : formState.get('billingAddress').get('country')
      });
      void calculateTax({
        billingAddress
      });
    }
  };
  const MaybeFutureChargesTotalContent = useMemo(() => {
    if (isZeroCheckout || shouldShowTermsOfServiceMessages) {
      return null;
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(MaybeFutureChargesTotals, {}), /*#__PURE__*/_jsx(HR, {})]
    });
  }, [isZeroCheckout, shouldShowTermsOfServiceMessages]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ContactInfo, {}), collectShippingAddress && /*#__PURE__*/_jsx(ShippingInfo, {}), isPartialPaymentsEnabled && /*#__PURE__*/_jsx(PaymentAmount, {}), /*#__PURE__*/_jsxs("div", {
      "data-tour-id": "payment-info-container",
      children: [/*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.paymentInfoHeader"
        })
      }), /*#__PURE__*/_jsx(PaymentElementSection, {
        resetSubmitError: submitObject.resetError
      }), /*#__PURE__*/_jsx(BillingInfo, {
        collectShippingAddress: collectShippingAddress,
        collectFullBillingAddress: collectFullBillingAddress,
        onChange: handleChange,
        stripePaymentMethod: stripePaymentMethod
      })]
    }), getIsAnyRecurring(checkoutSessionJS.lineItems, selectedOptionalLineItems) && iFrameLayout === LAYOUTS.EMBED && /*#__PURE__*/_jsx(Total, {
      checkoutSession: checkoutSession.toJS()
    }), shouldShowTermsOfServiceInputs && /*#__PURE__*/_jsx(TermsOfServiceInputs, {
      shouldShowTermsOfServiceAcknowledgementCheckbox: shouldShowTermsOfServiceAcknowledgementCheckbox,
      policySettings: policySettings,
      shouldDisplayPMOFCheckbox: shouldDisplayPMOFCheckbox,
      isZeroCheckout: isZeroCheckout,
      shouldShowClickToCancelCheckbox: shouldShowClickToCancelCheckbox
    }), /*#__PURE__*/_jsx(MaybeExpiredFutureLineItemsAlert, {}), /*#__PURE__*/_jsx(FormSubmitErrorMsgs, {
      submitEnabled: submitEnabled,
      showRequiredFieldsAlert: showRequiredFieldsAlert,
      setShowRequiredFieldsAlert: setShowRequiredFieldsAlert,
      submitObject: submitObject
    }), /*#__PURE__*/_jsx(ConfirmPaymentButton, {
      setShowRequiredFieldsAlert: setShowRequiredFieldsAlert,
      checkoutSession: checkoutSession,
      submitEnabled: submitEnabled,
      loadingBuyerOverrides: loadingBuyerOverrides,
      selectedOptionalLineItems: selectedOptionalLineItems,
      walletType: walletType,
      submitObject: submitObject,
      maximumAmountForPaymentMethod: maximumAmountForPaymentMethod
    }), shouldShowTermsOfServiceMessages && /*#__PURE__*/_jsx(TermsOfServiceMessages, {
      policySettings: policySettings,
      shouldDisplayPMOFMessage: shouldDisplayPMOFMessage,
      isZeroCheckout: isZeroCheckout
    }), MaybeFutureChargesTotalContent, /*#__PURE__*/_jsx(TLSEncryptionText, {})]
  });
};
export default PaymentContainer;